// inventoryOnboardingApi.js - API utilities for inventory item onboarding operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Helper function to extract pure Base64 data from image data URLs
 * Removes the "data:image/jpeg;base64," prefix if present
 */
const extractBase64Data = imageData => {
  if (!imageData) return null;

  // If it's already a pure Base64 string, return as is
  if (!imageData.includes('data:')) {
    return imageData;
  }

  // Extract the Base64 part after the comma
  const base64Match = imageData.match(/data:image\/[^;]+;base64,(.+)/);
  if (base64Match) {
    return base64Match[1];
  }

  return imageData;
};

/**
 * Convert base64 string to Blob
 */
const base64ToBlob = (base64, contentType = 'image/jpeg') => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log(
        'Successfully retrieved valid ID token for inventory onboarding API'
      );
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Get Presigned URL for Image Upload
 * Gets a presigned S3 upload URL for inventory item images
 * Endpoint: GET /inventory/image/presign
 * 
 * @param {Object} options - Optional parameters
 * @param {string} options.filename - Custom filename (optional)
 * @param {string} options.content_type - Image content type (defaults to 'image/jpeg')
 * @param {boolean} options.for_maintenance - Use maintenance-images folder (defaults to false)
 * @returns {Promise<Object>} Object with post_data and s3_url
 */
export const getPresignedImageUploadUrl = async (options = {}) => {
  try {
    const idToken = await getAuthToken();
    if (!idToken) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (options.filename) {
      queryParams.append('filename', options.filename);
    }
    if (options.content_type) {
      queryParams.append('content_type', options.content_type);
    }
    if (options.for_maintenance) {
      queryParams.append('for_maintenance', 'true');
    }

    const endpoint = `${API_BASE_URL}/inventory/image/presign${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    console.log('Getting presigned URL from:', endpoint);

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Presigned URL API Error:', errorText);
      
      let errorMessage = `HTTP ${response.status}: ${errorText}`;
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP ${response.status}: ${errorText}`;
      } catch {
        // If parsing fails, use the text as is
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/inventory/image/presign';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API error: ${response.status} for /inventory/image/presign`,
        error,
        {
          endpoint: '/inventory/image/presign',
          method: 'GET',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Presigned URL received:', {
      url: result.post_data?.url,
      s3_url: result.s3_url,
    });
    return result;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API network/fetch error for /inventory/image/presign`,
        error,
        {
          endpoint: '/inventory/image/presign',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Upload Image to S3 using Presigned URL
 * Uploads image data to S3 using the presigned POST data
 * 
 * @param {Object} postData - Presigned POST data from getPresignedImageUploadUrl
 * @param {Blob} imageBlob - Image file as Blob
 * @returns {Promise<void>}
 */
export const uploadImageToS3 = async (postData, imageBlob) => {
  try {
    console.log('Uploading image to S3...');
    console.log('S3 URL:', postData.url);
    console.log('Image size:', imageBlob.size, 'bytes');

    // Create FormData with presigned POST fields
    const formData = new FormData();
    
    // Add all fields from post_data.fields
    if (postData.fields) {
      Object.keys(postData.fields).forEach(key => {
        formData.append(key, postData.fields[key]);
      });
    }
    
    // Add the file (must be last)
    formData.append('file', imageBlob);

    const response = await fetch(postData.url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok && response.status !== 204) {
      const errorText = await response.text();
      console.error('S3 Upload Error:', errorText);
      const error = new Error(`S3 upload failed: HTTP ${response.status} - ${errorText}`);
      error.status = response.status;
      error.endpoint = 'S3 Upload';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API S3 upload error: ${response.status}`,
        error,
        {
          endpoint: 'S3 Upload',
          method: 'POST',
          status: response.status,
          responseData: errorText,
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.log('✅ Image uploaded to S3 successfully (Status:', response.status, ')');
    return;
  } catch (error) {
    console.error('Error uploading image to S3:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API network/fetch error for S3 upload`,
        error,
        {
          endpoint: 'S3 Upload',
          method: 'POST',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Create Inventory Item API
 * Creates a new inventory item record using the presigned S3 upload flow
 * Endpoint: POST /inventory/items
 * 
 * Flow:
 * 1. If imageData is provided, get presigned URL
 * 2. Upload image to S3 using presigned URL
 * 3. Create inventory item with picture_url from step 1
 */
export const createInventoryItem = async inventoryData => {
  try {
    const idToken = await getAuthToken();
    if (!idToken) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log('📦 ===== INVENTORY ITEM CREATION FLOW =====');

    // Step 1: Upload image to S3 if imageData is provided
    let pictureUrl = inventoryData.pictureUrl || null;
    
    if (!pictureUrl && inventoryData.imageData) {
      console.log('📸 Step 1: Uploading image to S3...');
      
      try {
        // Extract base64 data
        const base64Data = extractBase64Data(inventoryData.imageData);
        if (!base64Data || base64Data.length === 0) {
          throw new Error('Invalid image data');
        }

        // Detect content type from data URL if available
        let contentType = 'image/jpeg';
        if (inventoryData.imageData.includes('data:')) {
          const contentTypeMatch = inventoryData.imageData.match(/data:([^;]+)/);
          if (contentTypeMatch) {
            contentType = contentTypeMatch[1];
          }
        }

        console.log('Image details:', {
          base64Length: base64Data.length,
          estimatedSizeKB: Math.round((base64Data.length * 3) / 4 / 1024),
          contentType,
        });

        // Get presigned URL
        const presignResponse = await getPresignedImageUploadUrl({
          content_type: contentType,
        });

        if (!presignResponse.post_data || !presignResponse.s3_url) {
          throw new Error('Invalid presigned URL response');
        }

        // Convert base64 to Blob
        const imageBlob = base64ToBlob(base64Data, contentType);

        // Upload to S3
        await uploadImageToS3(presignResponse.post_data, imageBlob);

        // Use the s3_url from presign response
        pictureUrl = presignResponse.s3_url;
        console.log('✅ Image uploaded successfully. S3 URL:', pictureUrl);
      } catch (imageError) {
        console.error('❌ Image upload failed:', imageError);
        // Don't fail the entire request if image upload fails
        // Just log the error and continue without picture_url
        console.warn('⚠️ Continuing without image. Item will be created without picture_url.');
      }
    } else if (pictureUrl) {
      console.log('📸 Using provided picture_url:', pictureUrl);
    } else {
      console.log('📸 No image provided. Creating item without picture.');
    }

    // Step 2: Create inventory item
    console.log('📝 Step 2: Creating inventory item...');

    // Prepare payload - backend expects snake_case field names
    const sourceType = inventoryData.sourceType || inventoryData.source_type;
    const sourceId = inventoryData.sourceId || inventoryData.source_id;
    const originalSourceType = inventoryData.originalSourceType || inventoryData.sourceType || inventoryData.source_type;
    const originalSourceId = inventoryData.originalSourceId || inventoryData.sourceId || inventoryData.source_id;
    
    const requestBody = {
      name: inventoryData.name,
      description: inventoryData.description || '',
      type: inventoryData.type,
      status: inventoryData.status || 'good_condition',
      source_type: sourceType,
      source_id: sourceId,
      specifications: inventoryData.specifications || '',
      original_source_type: originalSourceType,
      original_source_id: originalSourceId,
      date_of_onboarding: inventoryData.dateOfOnboarding || new Date().toISOString(),
      // Only include picture_url if we have one
      ...(pictureUrl ? { picture_url: pictureUrl } : {}),
    };

    console.log('Request Body:', {
      ...requestBody,
      picture_url: pictureUrl || '(not provided)',
    });

    const endpoint = `${API_BASE_URL}/inventory/items`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      
      let errorMessage = `HTTP ${response.status}: ${errorText}`;
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP ${response.status}: ${errorText}`;
      } catch {
        // If parsing fails, use the text as is
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/inventory/items';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API error: ${response.status} for /inventory/items`,
        error,
        {
          endpoint: '/inventory/items',
          method: 'POST',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    const item = result.item || result;
    
    console.log('✅ Inventory item created successfully!');
    console.log('Item ID:', item?.item_id);
    console.log('Picture URL:', item?.picture_url || '(none)');
    console.log('📦 ===== END INVENTORY ITEM CREATION =====');
    
    return result;
  } catch (error) {
    console.error('❌ Error creating inventory item:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API network/fetch error for /inventory/items`,
        error,
        {
          endpoint: '/inventory/items',
          method: 'POST',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Sites API
 * Fetches available sites/locations from the system
 * Endpoint: /site/getall
 */
export const getSites = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/getall`);

    const response = await fetch(`${API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/site/getall';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API error: ${response.status} for /site/getall`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Sites fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching sites:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API network/fetch error for /site/getall`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    // Return empty array on error to prevent form from breaking
    return [];
  }
};

/**
 * Get All Vehicles API
 * Fetches all vehicles for the current user's tenant
 * Endpoint: /vehicle/getall
 */
export const getAllVehicles = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/vehicle/getall`);

    const response = await fetch(`${API_BASE_URL}/vehicle/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/vehicle/getall';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API error: ${response.status} for /vehicle/getall`,
        error,
        {
          endpoint: '/vehicle/getall',
          method: 'GET',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Vehicles fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API network/fetch error for /vehicle/getall`,
        error,
        {
          endpoint: '/vehicle/getall',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    // Return empty array on error to prevent form from breaking
    return [];
  }
};

/**
 * Get All Employees API
 * Fetches all employees/users from the system
 * Endpoint: /user/getall or similar
 */
export const getAllEmployees = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    // Try multiple possible endpoints
    const endpoints = [
      `${API_BASE_URL}/user/getall`,
      `${API_BASE_URL}/user/getAll`,
      `${API_BASE_URL}/user/list`,
    ];

    let lastError = null;
    for (const endpoint of endpoints) {
      try {
        console.log(`Calling endpoint: ${endpoint}`);

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Employees fetched successfully:', result);
          
          // Handle different response formats
          if (Array.isArray(result)) {
            return result;
          } else if (result.users && Array.isArray(result.users)) {
            return result.users;
          } else if (result.data && Array.isArray(result.data)) {
            return result.data;
          }
          
          return result;
        } else {
          const errorText = await response.text().catch(() => '');
          const errorData = await (async () => {
            try {
              return JSON.parse(errorText);
            } catch {
              return {};
            }
          })();
          
          lastError = new Error(
            `HTTP ${response.status}: ${response.statusText}`
          );
          lastError.status = response.status;
          lastError.endpoint = endpoint.replace(API_BASE_URL, '');
          lastError.responseData = errorData;
          
          // Log API error to logging service for each failed endpoint
          loggingService.logError(
            'api_error_inventory_onboarding',
            `Inventory Onboarding API error: ${response.status} for ${endpoint.replace(API_BASE_URL, '')}`,
            lastError,
            {
              endpoint: endpoint.replace(API_BASE_URL, ''),
              method: 'GET',
              status: response.status,
              responseData: errorData,
            }
          ).catch(logError => {
            console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
          });
        }
      } catch (err) {
        lastError = err;
        lastError.endpoint = endpoint.replace(API_BASE_URL, '');
        
        // Log network/fetch errors to logging service
        loggingService.logError(
          'api_error_inventory_onboarding',
          `Inventory Onboarding API network/fetch error for ${endpoint.replace(API_BASE_URL, '')}`,
          err,
          {
            endpoint: endpoint.replace(API_BASE_URL, ''),
            method: 'GET',
            errorType: err.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
        });
        
        continue;
      }
    }

    // If all endpoints failed, throw the last error
    if (lastError) {
      throw lastError;
    }

    return [];
  } catch (error) {
    console.error('Error fetching employees:', error);
    
    // Log final error if not already logged
    if (!error.endpoint) {
      loggingService.logError(
        'api_error_inventory_onboarding',
        `Inventory Onboarding API error for getAllEmployees`,
        error,
        {
          endpoint: '/user/getall (multiple attempts)',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[inventoryOnboardingApi] Failed to log error:', logError);
      });
    }
    
    // Return empty array on error to prevent form from breaking
    return [];
  }
};
